import { observer } from 'mobx-react';
import ApplyViewModel from './ApplyViewModel';
import { useParams } from 'react-router-dom';
import useAsyncEffect from 'use-async-effect';
import { Steps } from './ApplyModels';
import { Tag } from 'primereact/tag';
import { Appl } from '../../../../core/Appl';
import { Button } from '../../../../core/components/Index';
const ReviewApplication = observer(() => {
  const vm = Appl.Services.get<ApplyViewModel>('ApplyViewModel');

  let { memberId } = useParams();
  useAsyncEffect(async () => {
    await vm.loadStepAsync(Steps.ReviewApplication, memberId!);
  }, []);

  return (
    <>
      <div className="space-y-6 max-w-4xl">
        <h1 className="text-32px tracking--1percent text-neutral-800 font-semibold mb-4">Retirement Application Packet Review</h1>
        <p className="text-black text-15px tracking--1percent leading-26px">
          Your Online Application for Service Retirement packet consolidates all required information into one comprehensive document. This form has been generated based on the details you've provided or confirmed throughout the process.{' '}
        </p>
        <p className="text-black text-15px tracking--1percent leading-26px">
          Please take a moment to carefully review your application. After you click "<b>Looks Good</b>", you will no longer be able to navigate to earlier sections using the menu on the left. Any updates or changes will need to be made through this Review Application step.
        </p>
        <div className="flex justify-between items-center border-b pb-2 pt-2 text-15px text-black">
          <div className="flex items-center">
            <span className="font-normal">Application for Service Retirement</span>
          </div>
          <div className="flex items-center space-x-2">
            {vm.model.personalInformation.appSelfReviewed === 'Y' && (
              <a
                href={`/ora/application/${vm.memberId}/print/${vm.submissionId}`}
                onClick={(e) => {
                  e.preventDefault();
                  vm.navigate(`/ora/application/${vm.memberId}/print/${vm.submissionId}`);
                }}
                className="!text-nowrap flex justify-stretch cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 rounded-md"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    vm.navigate(`/ora/application/${vm.memberId}/print/${vm.submissionId}`);
                  }
                }}
              >
                <Tag
                  severity="success"
                  value="Reviewed"
                  className="!rounded-r-none"
                />
                <div className="border-2 border-primary-500 rounded-r-md px-1 flex items-center ">
                  <svg width="11" height="9" viewBox="0 0 11 9" fill="none" className="w-3 h-3">
                    <path d="M10.7698 0.734338C11.0767 1.04679 11.0767 1.55421 10.7698 1.86666L4.48482 8.26566C4.17794 8.57811 3.67956 8.57811 3.37267 8.26566L0.230164 5.06616C-0.0767213 4.75371 -0.0767213 4.24629 0.230164 3.93384C0.537049 3.62139 1.03543 3.62139 1.34232 3.93384L3.92997 6.56593L9.66014 0.734338C9.96702 0.421887 10.4654 0.421887 10.7723 0.734338H10.7698Z" fill="#557122" />
                  </svg>
                </div>
              </a>
            )}
            {vm.model.personalInformation.appSelfReviewed !== 'Y' && (
              <a
                href={`/ora/application/${vm.memberId}/print/${vm.submissionId}`}
                onClick={(e) => {
                  e.preventDefault();
                  vm.navigate(`/ora/application/${vm.memberId}/print/${vm.submissionId}`);
                }}
                className="!text-nowrap flex justify-stretch cursor-pointer focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-offset-2 rounded-md"
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    vm.navigate(`/ora/application/${vm.memberId}/print/${vm.submissionId}`);
                  }
                }}
              >
                <Tag
                  severity="warning"
                  value="Needs Review"
                  className="!rounded-r-none"
                />
                <div className="border-2 border-orange-500 rounded-r-md px-1 flex items-center ">
                  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" className="w-3 h-3">
                    <path
                      d="M5.4996 0.5C5.8047 0.5 6.08617 0.661144 6.24087 0.925421L10.8818 8.83224C11.0387 9.09866 11.0387 9.4274 10.8861 9.69382C10.7336 9.96025 10.4478 10.1257 10.1406 10.1257H0.858643C0.551395 10.1257 0.265632 9.96025 0.113082 9.69382C-0.0394677 9.4274 -0.0373191 9.09651 0.117379 8.83224L4.75834 0.925421C4.91304 0.661144 5.1945 0.5 5.4996 0.5ZM5.4996 3.2502C5.21384 3.2502 4.98394 3.4801 4.98394 3.76586V6.17228C4.98394 6.45804 5.21384 6.68794 5.4996 6.68794C5.78536 6.68794 6.01526 6.45804 6.01526 6.17228V3.76586C6.01526 3.4801 5.78536 3.2502 5.4996 3.2502ZM6.18715 8.06304C6.18715 7.88069 6.11471 7.70581 5.98577 7.57687C5.85683 7.44793 5.68195 7.37549 5.4996 7.37549C5.31725 7.37549 5.14237 7.44793 5.01343 7.57687C4.88449 7.70581 4.81205 7.88069 4.81205 8.06304C4.81205 8.24539 4.88449 8.42027 5.01343 8.54921C5.14237 8.67815 5.31725 8.75059 5.4996 8.75059C5.68195 8.75059 5.85683 8.67815 5.98577 8.54921C6.11471 8.42027 6.18715 8.24539 6.18715 8.06304Z"
                      fill="#CF7119"
                    />
                  </svg>
                </div>
              </a>
            )}
          </div>
        </div>
        <div className="flex space-x-4 mt-6">
          <Button
            label="Save & Continue" rounded
            onClick={async () => await vm.onReviewApplicationStepChangeAsync()}
            disabled={vm.model.personalInformation.appSelfReviewed !== 'Y'}
          />
        </div>
      </div>
    </>
  );
});
export default ReviewApplication;
